import React, { useEffect, useRef, useState } from 'react';

/* ────────────────────────────────────────────────────────────
   Scroll-driven workflow showcase.

   The section is taller than the viewport; an inner panel is
   pinned with position: sticky. As you scroll, the left-hand copy
   cross-fades from one workflow to the next while the right-hand
   visual slides in and its individual pieces converge into place.

   Scroll progress is written straight to CSS custom properties
   (--r on each row) inside a rAF callback, so the animation is
   frame-accurate without re-rendering React on every scroll event.
   React state only changes when the active index changes.
   ──────────────────────────────────────────────────────────── */

const REVEAL_SPAN = 0.45; // fraction of a slide's scroll slice used to assemble the visual
const TAIL_HOLD = 0.14;   // trailing scroll reserved for holding on the final slide

// ── Visuals ────────────────────────────────────────────────────

const PopulationVisual = () => {
  const rows = [
    ['Specialty Pharmacy: Biologics', '+22.4%', 'amber', true],
    ['Musculoskeletal: Orthopedic Surgery', '-3.1%', 'green', false],
    ['Behavioral Health: Outpatient', '+14.7%', 'amber', true],
    ['Preventive Care Compliance', '+8.2%', 'green', false],
  ];
  return (
    <div className="feature-card">
      <div className="live-header wf-part" style={{ '--d': 0.02, '--dx': '-20px', '--dy': '0px' }}>
        <div className="live-dot"></div>
        <span className="live-label">Cost Driver Analysis</span>
      </div>
      <div className="monitor-items">
        {rows.map(([label, value, tone, alert], i) => (
          <div
            key={label}
            className={`monitor-item wf-part ${alert ? 'monitor-item-alert' : ''}`}
            style={{ '--d': 0.1 + i * 0.09, '--dx': i % 2 ? '-34px' : '34px', '--dy': '14px', '--rot': i % 2 ? '-2deg' : '2deg' }}
          >
            <span className="monitor-item-label">{label}</span>
            <span className={`monitor-item-value monitor-item-value-${tone}`}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HighCostVisual = () => {
  const rows = [
    ['Claimant A', 'Oncology \u00b7 specialty infusion', '$412K', 34, '#258697'],
    ['Claimant B', 'Cardiac \u00b7 post-surgical', '$268K', 22, '#4E9EAC'],
    ['Claimant C', 'Neonatal intensive care', '$184K', 15, '#78AAB2'],
    ['All other members', '1,206 lives', '$358K', 29, '#C7DDE1'],
  ];
  return (
    <div className="feature-card">
      <div className="chart-header">
        <span className="chart-label wf-part" style={{ '--d': 0.02, '--dx': '-18px', '--dy': '0px' }}>
          Spend concentration
        </span>
        <span className="chart-value wf-part" style={{ '--d': 0.08, '--dx': '18px', '--dy': '0px' }}>
          3 members, 26% of spend
        </span>
      </div>

      <div className="wf-conc">
        {rows.map(([name, , , share, color], i) => (
          <span
            key={name}
            className="wf-conc-seg wf-part wf-grow"
            style={{ flex: share, background: color, '--d': 0.1 + i * 0.08 }}
          />
        ))}
      </div>

      <div className="wf-legend">
        {rows.slice(0, 3).map(([name, detail, amount, , color], i) => (
          <div
            key={name}
            className="wf-legend-row wf-part"
            style={{ '--d': 0.34 + i * 0.07, '--dx': '-26px', '--dy': '0px' }}
          >
            <span className="wf-legend-dot" style={{ background: color }} />
            <span className="wf-legend-name">
              {name} <span className="wf-sub">{detail}</span>
            </span>
            <span className="wf-legend-val">{amount}</span>
          </div>
        ))}
      </div>

      <p className="wf-note wf-part" style={{ '--d': 0.58, '--dy': '12px' }}>
        <span>Stop-loss attachment $250K</span>
        <span>1 claimant above</span>
      </p>
    </div>
  );
};

const ComplianceVisual = () => {
  const MARKS = { 3: 'filed', 15: 'due', 22: 'due', 30: 'filed' };
  const cells = [];
  for (let i = 0; i < 3; i += 1) cells.push({ id: `lead-${i}`, label: 28 + i, muted: true });
  for (let d = 1; d <= 31; d += 1) cells.push({ id: `day-${d}`, label: d, mark: MARKS[d] });

  return (
    <div className="feature-card">
      <div className="wf-cal-head">
        <span className="wf-cal-month wf-part" style={{ '--d': 0.02, '--dx': '-16px', '--dy': '0px' }}>
          October
        </span>
        <span className="wf-cal-sub wf-part" style={{ '--d': 0.06, '--dx': '16px', '--dy': '0px' }}>
          4 filings open
        </span>
      </div>

      <div className="wf-cal-dow" aria-hidden="true">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={`${d}-${i}`}>{d}</span>
        ))}
      </div>

      <div className="wf-cal-grid">
        {cells.map((c, i) => (
          <span
            key={c.id}
            className={`wf-cal-day wf-part${c.muted ? ' is-muted' : ''}${c.mark ? ` is-${c.mark}` : ''}`}
            style={{ '--d': 0.06 + Math.floor(i / 7) * 0.07 + (i % 7) * 0.012, '--dy': '12px', '--dx': '0px' }}
          >
            {c.label}
          </span>
        ))}
      </div>

      <div className="wf-cal-key wf-part" style={{ '--d': 0.56, '--dy': '14px' }}>
        <span><i className="wf-key wf-key-due" /> Form 5500, due October 15</span>
        <span><i className="wf-key wf-key-filed" /> Section 111, filed</span>
      </div>
    </div>
  );
};

const EngagementVisual = () => {
  const steps = [
    ['Week 1', 'Announcement email'],
    ['Week 1', 'Benefits guide'],
    ['Week 2', 'Manager talking points'],
    ['Week 4', 'Deadline reminder'],
  ];
  const stats = [['74%', 'open rate'], ['3,180', 'guide views'], ['91%', 'enrolled']];

  return (
    <div className="feature-card">
      <div className="chart-header">
        <span className="chart-label wf-part" style={{ '--d': 0.02, '--dx': '-18px', '--dy': '0px' }}>
          Open enrollment campaign
        </span>
        <span className="chart-value wf-part" style={{ '--d': 0.08, '--dx': '18px', '--dy': '0px' }}>
          4 weeks
        </span>
      </div>

      <div className="wf-rail">
        <span className="wf-rail-line wf-part wf-grow" style={{ '--d': 0.1 }} />
        {steps.map(([week, label], i) => (
          <div
            key={label}
            className="wf-step wf-part"
            style={{ '--d': 0.18 + i * 0.09, '--dy': '18px', '--dx': '0px' }}
          >
            <span className="wf-step-dot" />
            <span className="wf-step-week">{week}</span>
            <span className="wf-step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="wf-stats">
        {stats.map(([value, label], i) => (
          <div
            key={label}
            className="wf-stat wf-part"
            style={{ '--d': 0.46 + i * 0.08, '--dy': '20px' }}
          >
            <span className="wf-stat-value">{value}</span>
            <span className="wf-stat-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Content ────────────────────────────────────────────────────

const WORKFLOWS = [
  {
    key: 'highcost',
    tag: 'High-Cost Claimants',
    tagClass: 'feature-tag-rose',
    glowClass: 'feature-glow-rose',
    title: 'Catch high-cost claimants before the renewal does',
    desc: 'A small group drives most of the spend. Avenir flags high-cost, high-risk members early, projects their run rate, and tracks them against budget month over month.',
    Visual: HighCostVisual,
  },
  {
    key: 'population',
    tag: 'Population Health',
    tagClass: 'feature-tag-amber',
    glowClass: 'feature-glow-amber',
    title: 'See where the money is going, and why',
    desc: 'See which diagnostic categories are rising, where high-cost claimants concentrate, and how utilization compares with benchmark, with every driver tied to an intervention.',
    Visual: PopulationVisual,
  },
  {
    key: 'engagement',
    tag: 'Engagement & Communication',
    tagClass: 'feature-tag-purple',
    glowClass: 'feature-glow-purple',
    title: 'Open enrollment communication employees actually read',
    desc: 'Avenir turns plan changes into a full open enrollment campaign: announcement emails, a benefits guide built from the Summaries of Benefits and Coverage (SBCs), and a communication calendar tuned to how your population engages.',
    Visual: EngagementVisual,
  },
  {
    key: 'compliance',
    tag: 'Compliance & Filings',
    tagClass: 'feature-tag-amber',
    glowClass: 'feature-glow-amber',
    title: 'Filings, leave laws, and plan documents on one calendar',
    desc: 'Avenir tracks every deadline across jurisdictions and plan documents, and flags what is still open, so nothing slips through a regulatory crack.',
    Visual: ComplianceVisual,
  },
];

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

// ── Component ──────────────────────────────────────────────────

const WorkflowScroller = () => {
  const wrapRef = useRef(null);
  const rowRefs = useRef([]);
  const barRef = useRef(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);

  const setRow = (i) => (el) => { rowRefs.current[i] = el; };

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const n = WORKFLOWS.length;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pinned = window.matchMedia('(min-width: 901px)');

    const settle = () => {
      // Static mode: everything assembled, nothing pinned.
      rowRefs.current.forEach((el) => el && el.style.setProperty('--r', '1'));
      if (barRef.current) barRef.current.style.setProperty('--wp', '1');
    };

    const apply = () => {
      if (!pinned.matches || reduced.matches) {
        settle();
        return;
      }

      const rect = wrap.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const p = travel > 0 ? clamp01(-rect.top / travel) : 0;
      // The slides are spread over the first (1 - TAIL_HOLD) of the scroll, so
      // the last one sits fully assembled for a beat before the panel unpins.
      const t = clamp01(p / (1 - TAIL_HOLD)) * n;
      const idx = Math.min(Math.floor(t), n - 1);

      for (let i = 0; i < n; i += 1) {
        const local = clamp01(t - i);
        const el = rowRefs.current[i];
        if (el) el.style.setProperty('--r', String(clamp01(local / REVEAL_SPAN)));
      }

      // One continuous bar: full by the time the last slide has assembled,
      // so it does not sit at 99% through the tail hold.
      if (barRef.current) {
        barRef.current.style.setProperty('--wp', String(clamp01(t / n)));
      }

      if (idx !== activeRef.current) {
        activeRef.current = idx;
        setActive(idx);
      }
    };

    // Run synchronously. requestAnimationFrame does not fire while the tab is
    // not being painted, so any frame queued from a scroll in that state is
    // simply never run and the section sticks on one slide.
    const onScroll = apply;

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('visibilitychange', onScroll);
    reduced.addEventListener?.('change', onScroll);
    pinned.addEventListener?.('change', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', onScroll);
      reduced.removeEventListener?.('change', onScroll);
      pinned.removeEventListener?.('change', onScroll);
    };
  }, []);

  return (
    <div className="wf-scroll" ref={wrapRef} style={{ '--wf-count': WORKFLOWS.length }}>
      <div className="wf-sticky">
        <div className="wf-head section-center">
          <h2 className="section-heading">
            Pre-built for the work <span className="gradient-text">your team does</span>
          </h2>
        </div>

        <div className="wf-stage">
          {WORKFLOWS.map((w, i) => {
            const state = i === active ? 'is-active' : i < active ? 'is-past' : 'is-next';
            const { Visual } = w;
            return (
              <div
                key={w.key}
                ref={setRow(i)}
                className={`wf-row ${state}`}
                aria-hidden={i !== active}
              >
                <div className="wf-copy">
                  {/* desc stays in the data for search engines and screen readers */}
                  <h3 className="feature-title">{w.title}</h3>
                  <p className="sr-only">{w.desc}</p>
                </div>

                <div className="wf-visual feature-visual">
                  <div className={`feature-glow ${w.glowClass}`}></div>
                  <Visual />
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="wf-progress"
          ref={barRef}
          role="progressbar"
          aria-label="Workflow examples"
          aria-valuemin={1}
          aria-valuemax={WORKFLOWS.length}
          aria-valuenow={active + 1}
        >
          <span className="wf-progress-fill" />
        </div>
      </div>
    </div>
  );
};

export default WorkflowScroller;
